import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { FilePdf, FileXls, FileCsv, FileText, Receipt, Bank, Money, ListChecks, Envelope, ArrowSquareOut } from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EXPORTS = [
  { key: 'sales', label: 'Journal des ventes', desc: 'Toutes les factures émises avec HT/TVA/TTC', icon: Receipt, color: 'blue' },
  { key: 'purchases', label: 'Journal des achats', desc: 'Réceptions de stock avec prix d\'achat', icon: ListChecks, color: 'amber' },
  { key: 'cash', label: 'Journal de caisse', desc: 'Mouvements en espèces', icon: Money, color: 'green' },
  { key: 'bank', label: 'Journal de banque', desc: 'Mouvements CB / virement / chèque', icon: Bank, color: 'indigo' },
  { key: 'ledger', label: 'Écritures comptables', desc: 'Écritures en partie double (PCG)', icon: FileText, color: 'purple' },
];

const AccountingExports = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [token, setToken] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  // Detect if we're inside a sandboxed iframe (e.g. Emergent editor preview).
  // In that case downloads are blocked at the browser level — we surface a
  // helpful banner & alternative actions to the user.
  const [inSandboxedFrame, setInSandboxedFrame] = useState(false);
  useEffect(() => {
    try {
      setInSandboxedFrame(window.self !== window.top);
    } catch (e) {
      setInSandboxedFrame(true);
    }
  }, []);

  // Refresh download token every 8 minutes (token lives 10 min).
  const refreshToken = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/auth/download-token`, { withCredentials: true });
      setToken(r.data?.token || '');
    } catch (e) {
      setToken('');
    }
  }, []);

  useEffect(() => {
    refreshToken();
    const id = setInterval(refreshToken, 8 * 60 * 1000);
    return () => clearInterval(id);
  }, [refreshToken]);

  const buildUrl = (path) => {
    const sp = new URLSearchParams();
    if (startDate) sp.set('start_date', startDate);
    if (endDate) sp.set('end_date', endDate + 'T23:59:59');
    if (token) sp.set('token', token);
    return `${API_URL}/api/${path}?${sp.toString()}`;
  };

  // Open the app in a real top-level browser tab (outside any iframe).
  // This bypasses the Emergent editor sandbox so downloads work natively.
  const openInTopWindow = () => {
    try {
      window.top.location.href = window.location.href;
    } catch (e) {
      window.open(window.location.href, '_blank', 'noopener,noreferrer');
    }
  };

  const sendByEmail = async () => {
    setSendingEmail(true);
    try {
      const payload = {};
      if (startDate) payload.start_date = startDate;
      if (endDate) payload.end_date = endDate + 'T23:59:59';
      const r = await axios.post(`${API_URL}/api/accounting/email-exports`, payload, { withCredentials: true });
      toast.success(`Envoyé à ${(r.data.recipients || []).join(', ')} (${(r.data.files || []).length} fichiers)`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erreur lors de l\'envoi par email';
      if (typeof msg === 'string' && msg.toLowerCase().includes('destinataire')) {
        toast.error('Aucun destinataire configuré. Allez dans Paramètres → Comptabilité.');
      } else {
        toast.error(msg);
      }
    } finally {
      setSendingEmail(false);
    }
  };

  // Style classes shared between buttons (anchor styled as button)
  const linkGreen = 'flex-1 inline-flex items-center justify-center gap-1 px-4 py-2 rounded-md font-medium text-sm text-white bg-green-600 hover:bg-green-500 transition-colors';
  const linkBlue = 'flex-1 inline-flex items-center justify-center gap-1 px-4 py-2 rounded-md font-medium text-sm text-white bg-blue-600 hover:bg-blue-500 transition-colors';
  const linkRed = 'w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md font-medium text-sm text-white bg-red-600 hover:bg-red-500 transition-colors';
  const linkViolet = 'w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md font-medium text-sm text-white bg-violet-700 hover:bg-violet-600 transition-colors';

  return (
    <div className="App">
      <Sidebar />
      <div className="main-content" data-testid="accounting-exports-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Exports comptables</h1>
          <p className="text-slate-600">Exportez vos journaux et écritures comptables sur la période de votre choix</p>
        </div>

        {inSandboxedFrame && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-6" data-testid="sandbox-banner">
            <div className="flex items-start gap-3">
              <div className="text-2xl">⚠️</div>
              <div className="flex-1">
                <h3 className="font-semibold text-amber-900 mb-1">Téléchargements bloqués dans l&apos;éditeur Emergent</h3>
                <p className="text-sm text-amber-800 mb-3">
                  Votre navigateur bloque les téléchargements dans l&apos;aperçu (sandbox iframe). Deux solutions :
                </p>
                <div className="flex flex-col md:flex-row gap-2">
                  <Button
                    onClick={openInTopWindow}
                    data-testid="open-top-btn"
                    className="bg-amber-600 hover:bg-amber-500 text-white"
                  >
                    <ArrowSquareOut size={18} className="mr-2" />
                    Ouvrir l&apos;app en plein écran
                  </Button>
                  <Button
                    onClick={sendByEmail}
                    disabled={sendingEmail}
                    data-testid="email-all-btn"
                    className="bg-blue-600 hover:bg-blue-500 text-white"
                  >
                    <Envelope size={18} className="mr-2" />
                    {sendingEmail ? 'Envoi…' : 'M\'envoyer tous les exports par email'}
                  </Button>
                </div>
                <p className="text-xs text-amber-700 mt-2">
                  En plein écran (hors éditeur), les téléchargements fonctionnent normalement. Une fois l&apos;app déployée, cette restriction disparaît complètement.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-slate-900 mb-3">Période</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date début</label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="export-start-date" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date fin</label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="export-end-date" />
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-3">Laissez vide pour exporter toutes les données disponibles.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {EXPORTS.map((e) => {
            const Icon = e.icon;
            return (
              <div key={e.key} className="bg-white border border-slate-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3 mb-4">
                  <div className={`p-2 rounded-lg bg-${e.color}-50`}>
                    <Icon size={24} className={`text-${e.color}-600`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">{e.label}</h3>
                    <p className="text-sm text-slate-600 mt-1">{e.desc}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <a
                    href={buildUrl(`accounting/${e.key}/xlsx`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`export-${e.key}-xlsx`}
                    className={linkGreen}
                  >
                    <FileXls size={18} /> Excel
                  </a>
                  <a
                    href={buildUrl(`accounting/${e.key}/csv`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`export-${e.key}-csv`}
                    className={linkBlue}
                  >
                    <FileCsv size={18} /> CSV
                  </a>
                </div>
              </div>
            );
          })}

          <div className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 rounded-lg p-5 md:col-span-2">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 rounded-lg bg-red-100">
                <FilePdf size={24} className="text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900">FEC — Fichier des Écritures Comptables</h3>
                <p className="text-sm text-slate-700 mt-1">
                  Format officiel français (arrêté du 29/07/2013). Fichier texte UTF-8 séparé par <code>|</code>.
                  Obligatoire en cas de contrôle fiscal.
                </p>
              </div>
            </div>
            <a
              href={buildUrl('accounting/fec')}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="export-fec-btn"
              className={linkRed}
            >
              <FileText size={18} /> Télécharger le FEC
            </a>
          </div>

          <div className="bg-gradient-to-br from-violet-50 to-purple-50 border-2 border-violet-200 rounded-lg p-5 md:col-span-2">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 rounded-lg bg-violet-100">
                <FileXls size={24} className="text-violet-700" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900">Sous-traitance — Montants à régler aux techniciens</h3>
                <p className="text-sm text-slate-700 mt-1">
                  Liste des interventions effectuées sur la période avec montants dus par sous-traitant (technicien).
                  Totaux agrégés par technicien.
                </p>
              </div>
            </div>
            <a
              href={buildUrl('accounting/subcontractor/xlsx')}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="export-subcontractor-btn"
              className={linkViolet}
            >
              <FileXls size={18} /> Export sous-traitance (Excel)
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountingExports;
